import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AccessOrdererClient, CreateAccessOrderersRequest } from '@core/api';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverrideModule } from '@override/override.module';
import { IBaseSelectViewModelExtension } from '@shared/models/baseSelectViewModelExtension';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-add-access-orderer',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    CommonModule,
    SmartusComponentsModule,
    MatCheckboxModule,
    OverrideModule,
    TranslateModule,
    MatFormFieldModule,
    MatDialogModule,
  ],
  templateUrl: './add-access-orderer.component.html',
  styleUrl: './add-access-orderer.component.scss',
})
export class AddAccessOrdererComponent {
  employeeId: string;
  costCenterSelection: IBaseSelectViewModelExtension[];
  isLoading: boolean;
  hasCostCenterSelection: boolean;

  form = new FormGroup({});

  formModel: any = {};

  formFields: FormlyFieldConfig[] = [
    {
      key: 'employeeInformation',
      type: 'input',
      wrappers: ['readOnly'],
      props: {
        label: this.translateService.instant(
          'access_orderer.dialog.access_orderer'
        ),
        readOnly: true,
        valueFormatter: () => {
          let employeeString: string = '';

          if (this.data.selectedEmployee.fullName) {
            employeeString += this.data.selectedEmployee.fullName;
          }

          if (this.data.selectedEmployee.phone) {
            employeeString += ', ' + this.data.selectedEmployee.phone;
          }

          if (
            !this.data.selectedEmployee.phone &&
            this.data.selectedEmployee.mobilePhone
          ) {
            employeeString += ', ' + this.data.selectedEmployee.mobilePhone;
          }

          if (this.data.selectedEmployee.email) {
            employeeString += ', ' + this.data.selectedEmployee.email;
          }

          return employeeString;
        },
      },
    },
    {
      key: 'costCenterName',
      type: 'input',
      wrappers: ['readOnly'],
      props: {
        label: this.translateService.instant(
          'access_orderer.dialog.costcenter'
        ),
        readOnly: true,
        valueFormatter: () => {
          return this.data.selectedEmployee.costCenterName ?? '';
        },
      },
    },
    {
      key: 'accessOrdererEnddate',
      type: 'input',
      wrappers: ['readOnly'],
      props: {
        label: this.translateService.instant(
          'access_orderer.dialog.validUntil'
        ),
        readOnly: true,
        valueFormatter: (endDate: Date) => {
          const now = new Date();
          if (!this.data.endAccessOrderer) {
            return `${now.getDate()}. ${now.toLocaleDateString('de-DE', { month: 'long' })} ${now.getFullYear() + 1}`;
          } else if (!endDate) {
            return '--';
          }
          const r = moment(endDate)
            .locale(this.translateService.instant('locale.locale'))
            .format(
              this.translateService.instant('locale.moment__date_format')
            );
          return r;
        },
      },
    },
  ];

  constructor(
    private translateService: TranslateService,
    private accessOrdererClient: AccessOrdererClient,
    public dialogRef: MatDialogRef<AddAccessOrdererComponent>,
    public toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.employeeId = data.selectedEmployee.id;
    this.costCenterSelection = data.assignableCostCenters;
    this.hasCostCenterSelection = this.costCenterSelection?.length > 0;
  }

  submit(): void {
    const costCenterIds = this.costCenterSelection
      .filter(cc => cc.selected)
      .map(cc => cc.id);

    if (costCenterIds.length === 0) {
      return;
    }

    const request: CreateAccessOrderersRequest = {
      costCenterIds: costCenterIds,
      employeeId: this.employeeId,
    };

    this.isLoading = true;

    this.accessOrdererClient.createAccessOrderers(request).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: err => {
        console.log(err);
        this.isLoading = false;
        this.toastr.error(
          this.translateService.instant('generic.errorMessage')
        );
      },
    });
  }
}
